<template>
    <div class="row">
        <div class="col-12">
            <div class="card shadow my-2 bg-body rounded h-auto py-4 px-3 py-md-4 px-md-5">

                <Notifications :notifications="applicationSettings.notifications" @dismiss="dismissNotification" />

                <div v-if="isRedirecting || isAwaitingResult" class="text-center my-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading…</span>
                    </div>
                    <h4 class="mt-4">We are loading your results...</h4>
                </div>

                <template v-else>
                    <!-- Same loading layer as MainApp: the form's loading screens (submit messages,
                         the post-Flinks animation, ...) show here, and the form is hidden but kept
                         mounted meanwhile, so the step can't be clicked a second time mid-submit. -->
                    <LoadingAnimation v-if="loadingAnimation"
                        :showBrandLogo="!!applicationSettings.showBrandLogoDuringAnimation"
                        :showAffiliateLogo="!!applicationSettings.showAffiliateLogoDuringAnimation"
                        :brandLogo="projectBrand"
                        :message="applicationSettings.loadingAnimationMessage ?? null"
                        :image="applicationSettings.loadingAnimationImage ?? null"
                        :animationType="applicationSettings.loadingAnimationType ?? 'spinner'"
                    />

                    <div v-show="!loadingAnimation">
                        <IframeApplicationFlow v-if="showInitialFlow"
                            v-model:component-settings="initialFlowSettings"
                            :product-name="product"
                            :version="version"
                            :country="country"
                            :features="resolvedFeatures"
                        />

                        <ProcessedApplication v-else-if="hasProcessedApplication"
                            :application="processedApplication"
                            :mustRedirect="false"
                        />
                    </div>
                </template>

            </div>
        </div>
    </div>
</template>

<script setup>

/**
 * Thin root for the cross-domain iframe iframe.
 *
 * Reuses the shared form tree (IframeApplicationFlow -> LcForm -> fields and the
 * core/settings composables) but is its own root so MainApp.vue is left
 * untouched. Sets the small amount of app state the normal flow's lifecycle
 * would otherwise provide (product_name, country, forced phone verification).
 * On completion it navigates the iframe to the backend-resolved results URL
 * (mirroring production), falling back to an in-place ProcessedApplication when
 * there is no resultsUrl (e.g. the phone-gate fake lead).
 */

import { reactive, ref, computed, watch, onBeforeUnmount } from 'vue';

import Notifications from '@/components/main-app/Notifications.vue';
import LoadingAnimation from '@/components/main-app/LoadingAnimation.vue';
import IframeApplicationFlow from '@/components/flows/IframeApplicationFlow.vue';
import ProcessedApplication from '@/components/ProcessedApplication.vue';

import { useApplicationCore } from '@/composables/useApplicationCore.js';
import { createApplicationSettings, createFlowSettings } from '@/composables/useApplicationSettings.js';
import { provideApplicationSettings } from '@/composables/useApplicationSettingsProvider.js';
import { fireConversionPixel, iframeCompletionState, iframeDevStep, iframeFeatures, iframeFlinksDemo } from '@/form/helpers/iframe-config.js';

// Time to let the conversion pixel <script> load before navigating away from it.
const PIXEL_FIRE_DELAY_MS = 1500;

const props = defineProps({
    product: {
        type: String,
        default: 'personal_loan',
    },
    version: {
        type: String,
        default: 'v1',
    },
    country: {
        type: String,
        default: 'CA',
    },
    iframeToken: {
        type: String,
        default: '',
    },
    affiliateId: {
        type: [String, Number],
        default: null,
    },
    brandKey: {
        type: String,
        default: null,
    },
    // Optional step groups (flinks, enrollment) resolved server-side — see
    // app/Helpers/IframeFeatures.php. Everything else mirrors the site form.
    features: {
        type: Object,
        default: () => ({}),
    },
});

// Core app context (provide('app', ...)) reused unchanged by LcForm/LcField.
const core = useApplicationCore(props);
core.provideApp();

const { app, processedApplication, updateApplicationField } = core;

// The blade passes the resolved set as a prop; window.IframeApp.features is the
// fallback so the component is still correct if mounted without one.
const resolvedFeatures = { ...iframeFeatures(), ...props.features };

// State the normal flow's lifecycle would set; here we set it explicitly. The
// step set is derived from the site form, so it reads the same brand/country
// fields that form's conditions do — projectBrandKey drives the PQ-only
// requested-amount step, projectBrandCountry the country branches.
//
// Verification follows the server-resolved setting, which is clamped there to
// the product's own requirement: a phone-enforced product always arrives with
// verification=true, while a non-enforced one honours the iframe's setting
// (default true — stricter than the site, never weaker). The SMS step then
// shows/hides through the site step's own `!app.forceSmsVerification`
// condition, exactly as on the site.
const verificationForced = resolvedFeatures.verification !== false;

updateApplicationField('product_name', props.product);
updateApplicationField('projectBrandKey', props.brandKey);
updateApplicationField('projectBrandCountry', (props.country || 'CA').toUpperCase());
updateApplicationField('forceSmsVerification', verificationForced);
updateApplicationField('forcePhoneVerification', verificationForced);

// Dev step panel, on the same terms as the site form: ?dev_step=N|field_name
// plus APP_DEBUG. The site form gets this field through its lifecycle
// composable, which the stateless iframe does not run — so hand it over here
// and let LcForm apply the identical gate and jump.
const devStep = iframeDevStep();
if (devStep !== null) {
    updateApplicationField('dev_step', devStep);
}

// Flinks demo bank for QA, APP_DEBUG only (see iframeFlinksDemo).
if (iframeFlinksDemo()) {
    updateApplicationField('flinks_demo', '1');
}

// Settings (provided to the form tree).
const applicationSettings = reactive(createApplicationSettings({
    country: props.country,
    portalBaseUrl: null,
}));
applicationSettings.mustRedirect = false;
provideApplicationSettings(applicationSettings);

const flowSettings = createFlowSettings();
const initialFlowSettings = reactive(flowSettings.initialFlowSettings);

const isRedirecting = ref(false);

// The brand the server resolved for this iframe (window-inject), for the loading screen's logo.
const projectBrand = (typeof window !== 'undefined' && window.App?.project_brand) || null;
// Set once the completion has been acted on, so a late or repeated signal can
// never fire the pixel or navigate twice.
const completionHandled = ref(false);

// Computed
const showInitialFlow = computed(() => !initialFlowSettings.isCompleted);
const hasProcessedApplication = computed(() =>
    !!processedApplication.value && Object.keys(processedApplication.value).length > 0
);
// The form is done but the submit has not answered yet (see the watch below):
// show the loading state, not an empty card.
const isAwaitingResult = computed(() => initialFlowSettings.isCompleted && !hasProcessedApplication.value);

function dismissNotification(type) {
    applicationSettings.notifications[type] = {};
}

// Loading layer, mirroring MainApp (executeLoadingAnimation + its loadingAnimationSeconds
// watcher). Without it the form's loading screens never showed in the iframe, and the step
// stayed clickable during a submit: a second click on an auto-advancing last step emitted
// "completed", unmounted the form and killed the post-Flinks animation the submit was
// waiting on, so the final submit was never sent. Keep in step with MainApp.vue.
//   -1 => until set back to 0 (a request of unknown length)
//    0 => off
//   >0 => that many seconds, capped at 30
const loadingAnimation = computed(() => !!applicationSettings.showLoadingAnimation);
let loadingAnimationTimeoutId = null;

function executeLoadingAnimation(time) {
    if (time < 0) {
        applicationSettings.showLoadingAnimation = true;
    } else if (time == 0) {
        applicationSettings.showLoadingAnimation = false;
        if (loadingAnimationTimeoutId) {
            clearTimeout(loadingAnimationTimeoutId);
            loadingAnimationTimeoutId = null;
        }
    } else {
        const timeInMs = (time < 30) ? (time * 1000) : 30000;
        applicationSettings.showLoadingAnimation = true;
        if (loadingAnimationTimeoutId) {
            clearTimeout(loadingAnimationTimeoutId);
        }
        loadingAnimationTimeoutId = setTimeout(() => {
            applicationSettings.showLoadingAnimation = false;
            loadingAnimationTimeoutId = null;
        }, timeInMs);
    }
}

watch(
    () => applicationSettings.loadingAnimationSeconds,
    (seconds) => {
        if (typeof seconds === 'number') {
            executeLoadingAnimation(seconds);
        }
    }
);

onBeforeUnmount(() => {
    if (loadingAnimationTimeoutId) {
        clearTimeout(loadingAnimationTimeoutId);
    }
});

// Matched offers: mirror production. The synchronous submit has no buyers yet;
// they are presented on the page the CMI redirect resolves to. The backend
// returns that resolved internal results URL as `resultsUrl`; we navigate the
// iframe there (same origin) so the full thank-you / offers render. If there is
// no resultsUrl (e.g. the phone-gate fake lead), fall back to the in-place
// ProcessedApplication, which renders its own buyers.
// Act on completion only once BOTH signals are in: "completed" from the form
// and the submit's result. They can arrive in either order — a double trigger
// on the last step emits "completed" while the submit is still in flight — and
// acting on "completed" alone used to see an empty result: no pixel, no results
// page, and later a recovery link followed without the iframe context.
watch(
    () => [initialFlowSettings.isCompleted, processedApplication.value],
    ([completed, result]) => {
        if (completionHandled.value) {
            return;
        }
        const { state, url } = iframeCompletionState(completed, result);
        if (state === 'waiting') {
            return;
        }
        completionHandled.value = true;

        // Fire the CMI conversion pixel here. The normal form fires it on the
        // /redirect interstitial from session state, which the stateless iframe
        // never reaches — so without this the partner/affiliate is never credited.
        const pixelFired = fireConversionPixel(result?.pixelUrl);

        // Covers the lost-response fallback too (thankYouUrl), which would otherwise be
        // followed by ProcessedApplication without the iframe context.
        if (state === 'navigate') {
            isRedirecting.value = true;
            // Give the pixel a moment to load before navigating away from it.
            window.setTimeout(() => window.location.assign(url), pixelFired ? PIXEL_FIRE_DELAY_MS : 0);
        }
        // 'in-place' (e.g. the phone-gate fake lead) -> ProcessedApplication
        // renders it (fake leads carry no pixel, so nothing is lost).
    },
    { immediate: true }
);

</script>
